import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useApi } from '@/hooks/useApi';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, User, FileText, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PrescriptionDetailPage() {
  const { prescriptionId } = useParams();
  const { token } = useAuth();
  const api = useApi(token);
  const navigate = useNavigate();
  
  const [prescription, setPrescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [patientId, setPatientId] = useState(null);

  useEffect(() => {
    loadPrescription();
  }, [prescriptionId]);

  const loadPrescription = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/prescriptions/${prescriptionId}/detail`);
      setPrescription(data);
      setPatientId(data.patient_id);
    } catch (error) {
      console.error('Failed to load prescription:', error);
      toast.error('Failed to load prescription details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this prescription?')) return;
    
    try {
      await api.delete(`/prescriptions/${prescriptionId}`);
      toast.success('Prescription deleted successfully');
      navigate(-1);
    } catch (error) {
      console.error('Failed to delete prescription:', error);
      toast.error('Failed to delete prescription');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading prescription...</div>
        </div>
      </Layout>
    );
  }

  if (!prescription) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Prescription not found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Prescription Details</h1>
              <p className="text-sm text-gray-500">{prescription.source_filename}</p>
            </div>
          </div>
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>

        {/* Prescription Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Prescription Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {prescription.doctor_name && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Doctor</label>
                  <div className="flex items-center gap-2 mt-1">
                    <User className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">Dr. {prescription.doctor_name}</p>
                  </div>
                </div>
              )}
              
              {prescription.doctor_nmc_number && (
                <div>
                  <label className="text-sm font-medium text-gray-500">NMC Number</label>
                  <p className="text-gray-900 mt-1">{prescription.doctor_nmc_number}</p>
                </div>
              )}
              
              {prescription.prescription_date && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Prescription Date</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">{formatDate(prescription.prescription_date)}</p>
                  </div>
                </div>
              )}
              
              {prescription.follow_up_date && !prescription.no_followup && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Follow-up Date</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4 text-blue-400" />
                    <p className="text-blue-600 font-medium">{formatDate(prescription.follow_up_date)}</p>
                  </div>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-gray-500">Uploaded By</label>
                <p className="text-gray-900 mt-1">{prescription.uploaded_by || 'N/A'}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Upload Date</label>
                <p className="text-gray-900 mt-1">{formatDate(prescription.created_at)}</p>
              </div>
            </div>

            {prescription.follow_up_notes && (
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-500">Follow-up Notes</label>
                <p className="text-gray-900 mt-1 whitespace-pre-wrap">{prescription.follow_up_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Important Notes */}
        <Card className="bg-yellow-50 border-yellow-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-900">
              <AlertCircle className="h-5 w-5" />
              Important Notes for Next Visit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-yellow-900">
              <li className="flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>Please bring this prescription for reference</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>Bring all previous prescriptions and medical records</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>If you have recent lab reports, please bring them</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>Note any side effects or concerns to discuss with your doctor</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Preview & Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Document</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={async () => {
                  try {
                    const urlData = await api.get(`/files/${patientId}/files/${prescription.file_id}/download-url`);
                    window.open(urlData.url, '_blank');
                  } catch (error) {
                    console.error('Failed to load document:', error);
                    toast.error('Failed to load document');
                  }
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Preview Original Document
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Medicines List - Hidden for now */}
        {/* <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              Medicines ({prescription.medicines?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!prescription.medicines || prescription.medicines.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No medicines extracted</p>
            ) : (
              <div className="space-y-3">
                {prescription.medicines.map((medicine, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{medicine.name}</h3>
                        {medicine.dosage && (
                          <p className="text-sm text-gray-600 mt-1">Dosage: {medicine.dosage}</p>
                        )}
                        {medicine.frequency && (
                          <p className="text-sm text-gray-600">Frequency: {medicine.frequency}</p>
                        )}
                        {medicine.duration && (
                          <p className="text-sm text-gray-600">Duration: {medicine.duration}</p>
                        )}
                        {medicine.instructions && (
                          <p className="text-sm text-gray-500 mt-2 italic">{medicine.instructions}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="ml-4">
                        #{index + 1}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card> */}
      </div>
    </Layout>
  );
}
